import { Chakra } from "@airhelp/react-ui-components";
import { contentStyles } from "modules/storyblok/adapters/Content";
import RichtextAdapter from "modules/storyblok/adapters/RichtextAdapter";
import type { Richtext } from "storyblok-js-client";
import { Stars } from "../modules/common/components/Trustpilot/ReviewCard/Stars";
import { getInitials } from "../utils/stringUtils";

const { HStack, Text, Flex, Image, Box } = Chakra;

export interface TrustpilotReviewCardProps {
  reviewText: Richtext;
  author: string;
  authorsCountry: string;
  rating: string;
  avatarSrc: string;
  date?: string;
}

interface CardHeaderProps {
  rating: string;
  date: string;
}

interface CardFooterProps {
  avatarSrc: string;
  author: string;
  authorsCountry: string;
}

interface CardImageProps {
  avatarSrc: string;
  author: string;
}

const CardHeader = ({ rating, date }: CardHeaderProps) => (
  <Flex align="center" justify="space-between">
    {!!+rating && (
      <HStack spacing={1} mb={12}>
        <Stars amount={+rating} />
      </HStack>
    )}
    {date && <Text color="greyscale.500">{date}</Text>}
  </Flex>
);

interface CardBodyProps {
  reviewText: Richtext;
}

const CardBody = ({ reviewText }: CardBodyProps) =>
  reviewText && <RichtextAdapter content={reviewText} customStyles={contentStyles} />;

const CardImage = ({ avatarSrc, author }: CardImageProps) =>
  avatarSrc ? (
    <Image src={avatarSrc} w={12} h={12} mr={4} borderRadius="50%" loading="lazy" alt="" />
  ) : (
    <Flex
      w={12}
      h={12}
      borderRadius="50%"
      mr={4}
      justify="center"
      align="center"
      bg="greyscale.500"
      color="white"
      fontSize="lg"
      fontWeight="semibold"
    >
      {getInitials(author)}
    </Flex>
  );

const CardFooter = ({ avatarSrc, author, authorsCountry }: CardFooterProps) => (
  <Flex align="center" mt={12}>
    <CardImage avatarSrc={avatarSrc} author={author} />
    <Box>
      {author && <Text fontWeight="500">{author}</Text>}
      {authorsCountry && <Text color="greyscale.500">{authorsCountry}</Text>}
    </Box>
  </Flex>
);

const TrustpilotReviewCard = ({
  reviewText,
  author,
  authorsCountry,
  rating,
  avatarSrc,
  date,
}: TrustpilotReviewCardProps) => (
  <Flex flexDir="column">
    <CardHeader rating={rating} date={date} />
    <CardBody reviewText={reviewText} />
    {author && <CardFooter avatarSrc={avatarSrc} author={author} authorsCountry={authorsCountry} />}
  </Flex>
);

export default TrustpilotReviewCard;
